import React, { useEffect, useRef, useState } from 'react'
import { CButton, CFormInput, CSpinner, CTooltip } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilReload, cilSearch, cilX } from '@coreui/icons'

export interface WwSearchFieldProps {
  placeholder: string
  /** Consulta la entidad y devuelve los resultados a mostrar (máx. recomendado 10). */
  onBuscar: (texto: string) => Promise<any[]>
  renderItem: (item: any) => { titulo: string; subtitulo?: string; detalle?: string[] }
  onSeleccionar: (item: any) => void
  /** Si true, muestra botón X para limpiar la selección. */
  limpiar?: boolean
  onLimpiar?: () => void
  /** Si hay selección, muestra el resumen en vez del input. */
  seleccionado?: boolean
  resumen?: { titulo: string; lineas: string[] }
}

// DEBOUNCE 150ms (diseño RGM Abono Ágil): respuesta inmediata al teclear.
// Es global del componente compartido — lo usan AbonosPage (Abono Ágil) y
// PrestamosPage (búsqueda de cliente); 150ms no degrada ninguna de las dos
// (era 400ms, ajuste de UX, sin impacto en lógica).
const DEBOUNCE_MS = 150

const WwSearchField: React.FC<WwSearchFieldProps> = ({
  placeholder,
  onBuscar,
  renderItem,
  onSeleccionar,
  limpiar,
  onLimpiar,
  seleccionado,
  resumen,
}) => {
  const [texto, setTexto] = useState('')
  const [resultados, setResultados] = useState<any[]>([])
  const [abierto, setAbierto] = useState(false)
  const [buscando, setBuscando] = useState(false)
  const [error, setError] = useState('')
  const contenedorRef = useRef<HTMLDivElement>(null)
  const onBuscarRef = useRef(onBuscar)
  const reqRef = useRef(0)

  useEffect(() => {
    onBuscarRef.current = onBuscar
  })

  useEffect(() => {
    const alClicFuera = (e: MouseEvent) => {
      if (contenedorRef.current && !contenedorRef.current.contains(e.target as Node)) setAbierto(false)
    }
    document.addEventListener('mousedown', alClicFuera)
    return () => document.removeEventListener('mousedown', alClicFuera)
  }, [])

  const ejecutar = (valor: string) => {
    const t = valor.trim()
    if (!t) {
      setResultados([])
      setAbierto(false)
      setBuscando(false)
      setError('')
      return
    }
    const req = ++reqRef.current
    setBuscando(true)
    setError('')
    onBuscarRef.current(t)
      .then((r) => {
        if (reqRef.current !== req) return
        setResultados(r ?? [])
        setAbierto(true)
      })
      .catch((err: any) => {
        if (reqRef.current !== req) return
        setResultados([])
        setError(err?.message ?? 'Error al buscar')
        setAbierto(true)
      })
      .finally(() => {
        if (reqRef.current === req) setBuscando(false)
      })
  }

  useEffect(() => {
    if (!texto.trim()) {
      setResultados([])
      setAbierto(false)
      setBuscando(false)
      setError('')
      return
    }
    setBuscando(true)
    setError('')
    const timer = setTimeout(() => ejecutar(texto), DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [texto])

  const limpiarSeleccion = () => {
    setTexto('')
    setResultados([])
    setAbierto(false)
    setBuscando(false)
    setError('')
    onLimpiar?.()
  }

  return (
    <div ref={contenedorRef} className="position-relative">
      {seleccionado ? (
        <div className="border rounded-2 p-2" style={{ background: 'var(--cui-card-bg, #ffffff)' }}>
          <div className="d-flex justify-content-between align-items-start gap-2">
            <div className="flex-grow-1">
              <div className="fw-semibold">{resumen?.titulo}</div>
              {resumen?.lineas.map((linea, i) => (
                <div key={i} className="small text-body-secondary">
                  {linea}
                </div>
              ))}
            </div>
            {limpiar && (
              <CTooltip content="Limpiar selección" placement="top">
                <CButton size="sm" color="light" onClick={limpiarSeleccion}>
                  <CIcon icon={cilX} size="sm" />
                </CButton>
              </CTooltip>
            )}
          </div>
          <CButton size="sm" color="outline-secondary" className="mt-2" onClick={limpiarSeleccion}>
            <CIcon icon={cilReload} size="sm" className="me-1" />
            Cambiar
          </CButton>
        </div>
      ) : (
        <>
          <div className="input-group">
            <span className="input-group-text">
              <CIcon icon={cilSearch} size="sm" />
            </span>
            <CFormInput
              value={texto}
              placeholder={placeholder}
              onChange={(e) => setTexto(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  ejecutar(texto)
                }
                if (e.key === 'Escape') setAbierto(false)
              }}
            />
            {buscando && (
              <span className="input-group-text">
                <CSpinner size="sm" />
              </span>
            )}
            {texto && (
              <CTooltip content="Limpiar texto" placement="top">
                <CButton
                  color="light"
                  className="border"
                  onClick={() => {
                    setTexto('')
                    setResultados([])
                    setAbierto(false)
                    setError('')
                  }}
                >
                  <CIcon icon={cilX} size="sm" />
                </CButton>
              </CTooltip>
            )}
          </div>
          {abierto && (
            <div
              className="position-absolute start-0 end-0 border rounded-2 shadow-sm"
              style={{
                top: '100%',
                zIndex: 1060,
                maxHeight: 240,
                overflowY: 'auto',
                background: 'var(--cui-card-bg, #ffffff)',
              }}
            >
              {error && <div className="p-2 small text-danger">{error}</div>}
              {!error && buscando && resultados.length === 0 && (
                <div className="p-2 small text-body-secondary d-flex align-items-center gap-2">
                  <CSpinner size="sm" />
                  Buscando…
                </div>
              )}
              {!error && !buscando && resultados.length === 0 && (
                <div className="p-2 small text-body-secondary">Sin resultados para &ldquo;{texto.trim()}&rdquo;</div>
              )}
              {resultados.map((item, i) => {
                const { titulo, subtitulo, detalle } = renderItem(item)
                return (
                  <button
                    key={i}
                    type="button"
                    className="list-group-item list-group-item-action border-0 border-bottom"
                    onClick={() => {
                      onSeleccionar(item)
                      setResultados([])
                      setAbierto(false)
                      setError('')
                    }}
                  >
                    <div className="fw-semibold">{titulo}</div>
                    {subtitulo && <div className="small text-body-secondary">{subtitulo}</div>}
                    {detalle?.map((d, j) => (
                      <div key={j} className="small text-body-secondary">
                        {d}
                      </div>
                    ))}
                  </button>
                )
              })}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default WwSearchField
